import { apiClient } from "@/services/api/axiosClient";
import type { ApiResponse } from "@/services/api/api.types";
import { API_ENDPOINTS } from "@/services/api/endpoints";

/* -------------------------------------------------------------------------- */
/*                                    TYPES                                   */
/* -------------------------------------------------------------------------- */

export type SubmitSignaturePayload = {
  property_type: string;
  property_id: string;
  signature: string;
};

export type SubmitSignatureResponse = {
  status: string;
  message: string;
  data?: unknown;
};

export type SubmitSignatureBulkPropertyInfo = {
  property_id: string;
  property_type: string;
  homestead?: boolean;
  homestead_data?: {
    homestead_exemption_category: string;
    occupancy: string;
    occupancy_question: {
      question: string;
      address: string;
    };
  };
};

export type SubmitSignatureBulkPayload = {
  completed: boolean;
  property_info: SubmitSignatureBulkPropertyInfo[];
  send_email: boolean;
  signature: string;
};

export type SubmitHomesteadSignatureBulkPayload = {
  property_ids: string[];
  signature: string;
  send_email: boolean;
};

export type UploadKYCFilePayload = {
  property_id: string;
  document_type: string;
  /** Browser `File`, or React Native-style descriptor for native clients */
  file: File | { uri: string; type: string; name: string };
};

export type SubmitKYCDataPayload = {
  property_id: string;
  driving_licence_url: string;
};

export type SubmitKYCDataResponse = {
  status: string;
  message: string;
  data?: unknown;
};

export type UploadKYCFileResponse = {
  url?: string;
  driving_licence_url?: string;
};

export type VerifyLastNamePayload = {
  property_id: string;
  last_name: string;
};

export type VerifyLastNameResponse = {
  is_valid: boolean;
  message: string;
  matched_owners: unknown[] | null;
};

/* -------------------------------------------------------------------------- */
/*                                  SERVICES                                  */
/* -------------------------------------------------------------------------- */

export const signatureService = {
  /**
   * Submit signature for property protest documents
   */
  async submitSignature(
    payload: SubmitSignaturePayload
  ): Promise<ApiResponse<SubmitSignatureResponse>> {
    const { data } = await apiClient.post<ApiResponse<SubmitSignatureResponse>>(
      `/api/property-service/documents/signed`,
      payload
    );
    return data;
  },

  async submitSignatureBulk(
    payload: SubmitSignatureBulkPayload
  ): Promise<ApiResponse<SubmitSignatureResponse>> {
    const { data } = await apiClient.post<ApiResponse<SubmitSignatureResponse>>(
      `/api/property-service/documents/bulk/signed`,
      payload
    );
    return data;
  },

  async submitHomesteadSignatureBulk(
    payload: SubmitHomesteadSignatureBulkPayload
  ): Promise<ApiResponse<SubmitSignatureResponse>> {
    const { data } = await apiClient.post<ApiResponse<SubmitSignatureResponse>>(
      `/api/property-service/documents/homestead-exemption-sign/bulk`,
      payload
    );
    return data;
  },

  /**
   * Upload KYC file (driving license, formation document, etc.)
   * Uses FormData for file upload
   */
  async uploadKYCFile(payload: UploadKYCFilePayload): Promise<ApiResponse<UploadKYCFileResponse>> {
    const formData = new FormData();
    formData.append("property_id", payload.property_id);
    formData.append("document_type", payload.document_type);
    if (payload.file instanceof File) {
      formData.append("file", payload.file);
    } else {
      formData.append("file", {
        uri: payload.file.uri,
        type: payload.file.type,
        name: payload.file.name,
      } as unknown as Blob);
    }

    const { data } = await apiClient.post<ApiResponse<UploadKYCFileResponse>>(
      `/api/property-service/kyc/property-protest/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  },

  /**
   * Upload homestead document (driving license front/back, deed/closing documents)
   */
  async uploadHomesteadDocument(payload: {
    property_id: string;
    document_type: string;
    document_side?: string;
    file: File | { uri: string; type: string; name: string };
  }): Promise<ApiResponse<unknown>> {
    const formData = new FormData();
    formData.append("property_id", payload.property_id);
    formData.append("document_type", payload.document_type);
    formData.append("document_side", payload.document_side || "front_side");
    if (payload.file instanceof File) {
      formData.append("file", payload.file);
    } else {
      formData.append("file", {
        uri: payload.file.uri,
        type: payload.file.type,
        name: payload.file.name,
      } as unknown as Blob);
    }

    const { data } = await apiClient.post<ApiResponse<unknown>>(
      API_ENDPOINTS.PROPERTY.HOMESTEAD_DOCUMENTS,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  },

  /**
   * Submit KYC data with driving license URL
   */
  async submitKYCData(payload: SubmitKYCDataPayload): Promise<ApiResponse<SubmitKYCDataResponse>> {
    const { data } = await apiClient.post<ApiResponse<SubmitKYCDataResponse>>(
      `/api/property-service/kyc/property-protest`,
      payload
    );
    return data;
  },

  /**
   * Verify last name against property owners
   */
  async verifyLastName(
    payload: VerifyLastNamePayload
  ): Promise<ApiResponse<VerifyLastNameResponse>> {
    const { data } = await apiClient.post<ApiResponse<VerifyLastNameResponse>>(
      `/api/user-service/validate/owner/lastname`,
      payload
    );
    return data;
  },
};
